// ------------------------------------------------------------
// OnboardingWizard.tsx — Branching stepper
// ------------------------------------------------------------
// Flow:
//   Step 0: PlanComparison (Free vs Premium picker)
//     ├─ Picks Free → SlimFreeOnboarding → finish (creates placeholder
//     │              family members + activates free tier, lands /dashboard)
//     └─ Picks Premium → existing Preferences → Family → Review → /membership
//
import React, { useEffect, useState } from "react";
import { Box, Stepper, Step, StepLabel, Alert } from "@mui/material";
import PlanComparisonStep from "./PlanComparisonStep";
import SlimFreeOnboardingStep, { SlimFreeData } from "./SlimFreeOnboardingStep";
import PreferencesStep, { PreferencesData } from "./PreferencesStep";
import FamilyStep from "./FamilyStep";
import ReviewStep from "./ReviewStep";
import api from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { trackEvent, AnalyticsEvents } from "../../services/analytics";

type UserPath = 'free' | 'premium' | null;

const OnboardingWizard: React.FC = () => {
  const { user, setUser, refreshUser } = useAuth();
  const [userPath, setUserPath] = useState<UserPath>(null);
  const [active, setActive] = useState(0);
  const [preferencesData, setPreferencesData] = useState<PreferencesData | null>(null);
  const [familyData, setFamilyData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle return from Stripe Checkout. ?stripe=success → premium purchase
  // completed; jump into the wizard. ?stripe=canceled → stay on Plan Comparison
  // and surface a friendly error.
  useEffect(() => {
    const stripeStatus = searchParams.get('stripe');
    if (stripeStatus === 'success') {
      setUserPath('premium');
      setActive(1);
      refreshUser?.();
      setSearchParams({}, { replace: true });
    } else if (stripeStatus === 'canceled') {
      setError('Checkout was canceled. You can try again or pick the Free plan.');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume logic: if user already has a paid membership (Stripe purchase
  // completed but they bailed before finishing the wizard), skip Plan Comparison.
  useEffect(() => {
    if (userPath !== null) return;
    if (active !== 0) return;
    const status = user?.membership?.status;
    if (status === 'paid' || status === 'lapsed') {
      setUserPath('premium');
      setActive(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.membership?.status]);

  // Guard: if user already finished onboarding, bounce to dashboard
  if (user?.profileComplete) {
    return <Navigate to="/dashboard" replace />;
  }

  const handlePathSelect = (path: 'free' | 'premium') => {
    setUserPath(path);
    setActive(1);
  };

  const handlePreferencesNext = (data: PreferencesData) => {
    setPreferencesData(data);
    setActive(2);
  };

  const handleFamilyNext = (data: any) => {
    setFamilyData(data);
    setActive(3);
  };

  /**
   * Free-tier finish: save minimal user prefs, create placeholder family
   * members (Adults, Kids) with the chosen allergies, mark profile complete,
   * activate free membership, route to dashboard.
   */
  const handleFreeFinish = async (data: SlimFreeData) => {
    setSaving(true);
    setError(null);
    try {
      // Set minimal user preferences (defaults — user can refine later)
      await api.patch('/users/profile', {
        max_prep_time: 45,
        likes_leftovers: true,
        leftover_frequency: 50,
      });

      const allergyRestrictions = data.allergyTagIds.map(id => ({
        tag_id: id,
        severity: 'strict',
      }));

      // Create one placeholder family member per group, sized by count.
      // is_child differentiates so recipe filtering (kid-friendly checks) works.
      // Members are intentionally named "Adults" / "Kids" — the upgrade-to-
      // Premium wizard will load these and let the user rename/split them.
      const placeholders: Array<{ name: string; is_child: boolean; servings: number }> = [];
      if (data.numAdults > 0) placeholders.push({ name: 'Adults', is_child: false, servings: data.numAdults });
      if (data.numKids > 0) placeholders.push({ name: 'Kids', is_child: true, servings: data.numKids });

      for (const p of placeholders) {
        // Note: servings is not directly a field on familymembers — it represents
        // how many people the entry covers. We add as many duplicates as the count
        // dictates so existing serving-size math (one member = one mouth) works
        // without backend changes. For households of 1, this is just a single row.
        for (let i = 0; i < p.servings; i++) {
          await api.post('/family-members', {
            name: p.servings > 1 ? `${p.name.slice(0, -1)} ${i + 1}` : p.name.slice(0, -1), // "Adults" → "Adult 1", "Adult 2"
            is_child: p.is_child,
            dietary_restrictions: allergyRestrictions,
            meal_schedule: { breakfast: [], lunch: [], dinner: [] },
          });
        }
      }

      await api.put('/users/profile-complete');
      // Activate free membership (bypasses /membership detour)
      await api.post('/memberships/start-free');

      // Refresh user from /users/profile so client-side state includes the
      // newly-created membership row. Without this, PrivateRoute in App.tsx
      // sees user.membership === null and bounces to /membership (a second
      // paywall). setUser({...u, profileComplete: true}) alone doesn't update
      // the membership field.
      await refreshUser?.();
      trackEvent(AnalyticsEvents.ONBOARDING_COMPLETE, { path: 'free' });

      navigate('/dashboard');
    } catch (err: any) {
      console.error('Free onboarding error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Premium-tier finish: existing flow — save preferences, family members,
   * mark profile complete, route to /membership for Stripe checkout.
   */
  const handlePremiumFinish = async () => {
    setSaving(true);
    setError(null);

    try {
      if (preferencesData) {
        await api.patch('/users/profile', {
          max_prep_time: preferencesData.max_prep_time,
          likes_leftovers: preferencesData.likes_leftovers,
          leftover_frequency: preferencesData.leftover_frequency,
        });

        await api.patch('/users/meal-plan-start-day', {
          meal_plan_start_day: preferencesData.meal_plan_start_day,
        });
      }

      for (const m of familyData.members) {
        const restrictions = [
          ...m.restrictionTagIds.map((id: number) => ({ tag_id: id, severity: 'strict' })),
          ...m.medicalTagIds.map((id: number) => ({ tag_id: id, severity: 'strict' })),
          ...m.allergyTagIds.map((id: number) => ({ tag_id: id, severity: 'strict' })),
        ].filter(r => r.tag_id);

        const { data: memberResponse } = await api.post('/family-members', {
          name: m.name,
          is_child: m.isChild,
          dietary_restrictions: restrictions,
          meal_schedule: m.mealSchedule,
        });

        const memberId = memberResponse.familyMember?.member_id;
        if (memberId) {
          const allDislikes = [
            ...(m.otherAllergies || []),
            ...(m.ingredientDislikes || []),
          ];
          if (allDislikes.length > 0) {
            await api.put(`/family-members/${memberId}/ingredient-dislikes`, {
              ingredient_names: allDislikes,
            });
          }
        }
      }

      await api.put('/users/profile-complete');
      setUser((u) => u ? { ...u, profileComplete: true } : u);
      trackEvent(AnalyticsEvents.ONBOARDING_COMPLETE, { path: 'premium' });

      // User has ALREADY paid on the Plan Comparison screen (or via promo).
      // Skip /membership and land directly on the dashboard.
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Premium onboarding error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Stepper labels adapt to the chosen path
  const stepperLabels = userPath === 'free'
    ? ['Choose Plan', 'Quick Setup']
    : userPath === 'premium'
      ? ['Choose Plan', 'Preferences', 'Family', 'Review']
      : ['Choose Plan', '…'];

  return (
    <Box maxWidth={900} mx="auto" mt={4} px={2}>
      <Stepper activeStep={active} alternativeLabel sx={{ mb: 4 }}>
        {stepperLabels.map((l, i) => (
          <Step key={`${l}-${i}`}><StepLabel>{l}</StepLabel></Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {active === 0 && (
        <PlanComparisonStep onSelect={handlePathSelect} />
      )}

      {/* Free path */}
      {userPath === 'free' && active === 1 && (
        <SlimFreeOnboardingStep
          onBack={() => { setActive(0); setUserPath(null); }}
          onNext={handleFreeFinish}
          saving={saving}
        />
      )}

      {/* Premium path */}
      {/* No Back to Plan Comparison: user has already paid via Stripe Checkout.
          Re-clicking Premium there would start a second Checkout session and could
          create a duplicate subscription. They can manage their plan from account settings. */}
      {userPath === 'premium' && active === 1 && (
        <PreferencesStep onNext={handlePreferencesNext} />
      )}
      {userPath === 'premium' && active === 2 && (
        <FamilyStep onBack={() => setActive(1)} onNext={handleFamilyNext} />
      )}
      {userPath === 'premium' && active === 3 && (
        <ReviewStep
          data={familyData}
          onBack={() => setActive(2)}
          onFinish={handlePremiumFinish}
          saving={saving}
        />
      )}
    </Box>
  );
};

export default OnboardingWizard;
